import {
  selectAppTypesGroups,
  selectDevices,
  selectMarkets,
  selectNotifications,
  selectUsers
} from "../../../../../state/selectors";
import { getState } from "../../../../../state/store";

import { campaignCategoriesOptions } from "../../../../../consts";
import { deviceModels } from "../../../consts/deviceModels";
import { audienceSetOptions } from "../consts/audienceSetOptions";
import { adminUserUtils, dateUtil } from "../../../../../utils";
import { appUserService } from "../../../../../services";

const toApiRequest = (notificationCampaignData) => {
  const { overviewData, audienceData } = notificationCampaignData;

  const isAppSetOption = audienceData?.audienceSetOption === audienceSetOptions.APP;

  const markets =
    audienceData.selectedMarkets.filter((market) => market.id).map((market) => market.id) ?? [];
  const appTypes = isAppSetOption ? audienceData.selectedApps?.map((app) => app.id) ?? [] : [];
  const deviceTypes = !isAppSetOption
    ? audienceData.selectedDevices?.map((device) => device.id) ?? []
    : [];

  const dusns = audienceData?.selectedDusns.map((dusn) => dusn.id) ?? [];
  const userIds = audienceData?.selectedUsers.map((user) => user.id) ?? [];

  const firmwareVersions = audienceData.versions?.map((version) => version.id);

  const startDateTime = dateUtil.combineDateTime(overviewData.startDate, overviewData.startTime);

  const requestData = {
    campaignName: overviewData.campaignName,
    notificationType: overviewData?.selectedCategory?.id,
    messageName: overviewData?.selectedNotificationMessage?.id,
    details: {
      appTypes,
      deviceTypes,
      firmwareVersions
    },
    markets,
    dusns,
    userIds,
    startTs: dateUtil.toDateStringNoTimezone(startDateTime)
  };

  if (!dusns.length) {
    delete requestData.dusns;
  }

  if (!userIds.length) {
    delete requestData.userIds;
  }

  if (!appTypes.length) {
    delete requestData.details.appTypes;
  }

  if (!deviceTypes.length) {
    delete requestData.details.deviceTypes;
  }

  if (!firmwareVersions.length) {
    delete requestData.details.firmwareVersions;
  }

  return requestData;
};

const parseNotificationCampaignData = (notificationCampaignData) => {
  const userMarkets = selectMarkets()(getState());
  const apps = selectAppTypesGroups()(getState());
  const notifications = selectNotifications()(getState());
  const devices = selectDevices()(getState());
  const users = selectUsers()(getState());

  const notificationMessagesOptions = notifications?.map((notification) => {
    return {
      id: notification.notificationName,
      value: notification.notificationName
    };
  });

  const deviceOptions = Object.keys(deviceModels)?.map((device) => {
    return { id: device, value: device };
  });

  const dusnOptions = devices?.map((device) => {
    return { id: device?.dusn, value: device?.dusn };
  });

  const userOptions = users
    ?.filter((user) => !adminUserUtils.isUserAdmin(user))
    ?.map((user) => {
      const name = `${user?.firstName} ${user?.lastName}`;
      return { id: user?.cognitoId, value: `${name} ${user?.email}` };
    });

  const marketOptions = (notificationCampaignData.markets ?? [])
    ?.map((m) => {
      return {
        id: m,
        value: m,
        isAvailable: appUserService.isAdmin() || userMarkets?.some((market) => market.market === m)
      };
    })
    .sort((first, second) => {
      return first.isAvailable === second.isAvailable ? 0 : second.isAvailable ? -1 : 1; // disabled comes first
    });

  const appOptions = (notificationCampaignData?.audience?.appTypes ?? [])
    ?.map((app) => {
      return {
        id: app,
        value: app,
        isAvailable: appUserService.isAdmin() || apps?.some((a) => a.appType === app)
      };
    })
    .sort((first, second) => {
      return first.isAvailable === second.isAvailable ? 0 : second.isAvailable ? -1 : 1; // disabled comes first
    });

  const startDate = dateUtil.getUTCDate(new Date(notificationCampaignData?.startTs));

  const selectedCategory = campaignCategoriesOptions.find(
    (category) => category.id === notificationCampaignData?.notificationType
  );
  const selectedNotificationMessage = notificationMessagesOptions.find(
    (notification) => notification.id === notificationCampaignData?.messageName
  );

  const overviewData = {
    campaignName: notificationCampaignData?.campaignName,
    selectedCategory,
    selectedNotificationMessage,
    startDate: startDate,
    startTime: startDate
  };

  const markets = notificationCampaignData?.markets ?? [];
  const selectedMarkets = marketOptions.filter((opt) => markets.includes(opt.id));

  const details = notificationCampaignData?.details ?? {};

  const audienceSetOption = notificationCampaignData?.dusns?.length
    ? audienceSetOptions.DUSN
    : notificationCampaignData?.userIds?.length
    ? audienceSetOptions.USER
    : details?.appTypes?.length
    ? audienceSetOptions.APP
    : audienceSetOptions.DEVICE_TYPE;

  const selectedApps = appOptions?.filter((opt) => details?.appTypes?.includes(opt.id));
  const selectedDevices = deviceOptions?.filter((opt) => details?.deviceTypes?.includes(opt.id));
  const selectedDusns = dusnOptions?.filter((opt) =>
    notificationCampaignData?.dusns?.includes(opt.id)
  );
  const selectedUsers = userOptions?.filter((opt) =>
    notificationCampaignData?.userIds?.includes(opt.id)
  );

  const versions = (details?.firmwareVersions ?? []).map((version) => {
    return { id: version, value: version };
  });

  const audienceData = {
    audienceSetOption,
    selectedMarkets,
    selectedApps,
    selectedDevices,
    selectedDusns,
    selectedUsers,
    versions
  };

  return { overviewData, audienceData };
};

export const formatUtils = { toApiRequest, parseNotificationCampaignData };
